import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import GeneralContactForm from "../components/GeneralContactForm";
import { BUSINESS_INFO, IMAGES, PRODUCTS_AND_EQUIPMENT } from "../data";

export default function DoedsboRengoering() {
  return (
    <>
      <SeoHead
        title="Tømning af dødsbo, derefter rengøring"
        description="Værdig og professionel tømning af dødsbo i Norddjurs med efterfølgende grundig slutrengøring. Vi rydder, sorterer og rengør med stor respekt og omsorg."
        path="/toemning-af-doedsbo"
      />

      {/* SUBPAGE HERO */}
      <section className="bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2] py-12 lg:py-20 border-b border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 bg-[#EADFCF] text-[#4B3C2D] px-3.5 py-1 rounded-full text-xs font-semibold">
                <i className="fa-solid fa-boxes-packing text-[#887055]" aria-hidden="true"></i>
                <span>Omsorgsfuld service i Norddjurs</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading text-[#2C2523] tracking-tight">
                Tømning af dødsbo, derefter rengøring
              </h1>

              <p className="text-base text-[#5C524C] leading-relaxed max-w-2xl">
                En værdig, nænsom og professionel håndtering af dødsbo. Vi hjælper med komplet tømning, bortskaffelse og grundig slutrengøring, så boligen afleveres skinnende ren og klar til overdragelse.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={`tel:${BUSINESS_INFO.phone}`}
                  className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2 shadow-xs active:scale-[0.98] whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring 40 94 75 40</span>
                </a>
                <a
                  href="#bestil-form"
                  className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-colors border border-[#D8C7B0]"
                >
                  Bestil eller indhent tilbud
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-[#EADFCF] shadow-lg relative group">
                <img
                  src={IMAGES.estateClearance}
                  alt="Tømning af dødsbo og efterfølgende rengøring i Norddjurs"
                  className="w-full h-[340px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-5">
                  <p className="text-white text-xs sm:text-sm font-medium flex items-center gap-2">
                    <i className="fa-solid fa-handshake-angle text-[#EADFCF]" aria-hidden="true"></i>
                    Tryghed, punktlighed og fuld diskretion
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* DETAILED PROCESS SECTION */}
      <section className="py-14 lg:py-20 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#887055]">
              Vores forløb
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#2C2523]">
              Sådan hjælper vi dig fra start til slut
            </h2>
            <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed">
              At stå med et dødsbo kan være en uoverskuelig opgave. Hos Ses Multi Service tager vi hånd om hele processen med stor empati, ordentlighed og respekt for jeres ønsker.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-3 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-lg font-bold">
                1
              </div>
              <h3 className="text-lg font-bold font-heading text-[#2C2523]">
                Tryg besigtigelse
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Vi mødes i boligen til en uforpligtende samtale og gennemgang. I bestemmer hvad der skal gemmes, og hvad vi skal tage os af.
              </p>
            </div>

            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-3 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-lg font-bold">
                2
              </div>
              <h3 className="text-lg font-bold font-heading text-[#2C2523]">
                Tømning & sortering
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Vi rydder hele boligen, loftsrum, kælder eller garage. Alt sorteres ansvarligt til genbrug, donation eller godkendt bortskaffelse.
              </p>
            </div>

            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-3 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-lg font-bold">
                3
              </div>
              <h3 className="text-lg font-bold font-heading text-[#2C2523]">
                Grundig slutrengøring
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Efter tømningen rengør vi boligen fra top til bund: aftørring, støvsugning, gulvvask, afkalkning af badeværelse og rens af køkken.
              </p>
            </div>

            <div className="bg-[#FFFDF9] rounded-2xl border border-[#EADFCF] p-6 space-y-3 shadow-2xs hover:border-[#887055] transition-colors">
              <div className="w-12 h-12 rounded-xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-lg font-bold">
                4
              </div>
              <h3 className="text-lg font-bold font-heading text-[#2C2523]">
                Klar til overdragelse
              </h3>
              <p className="text-xs sm:text-sm text-[#5C524C] leading-relaxed">
                Boligen efterlades 100% ren, pæn og fuldstændig klar til syn med udlejer, salg gennem ejendomsmægler eller overdragelse.
              </p>
            </div>

          </div>

          {/* Special highlights */}
          <div className="bg-[#F4EFE6] rounded-3xl p-6 sm:p-10 border border-[#EADFCF] space-y-6">
            <h3 className="text-xl font-bold font-heading text-[#2C2523] flex items-center gap-2.5">
              <i className="fa-solid fa-heart text-[#887055]" aria-hidden="true"></i>
              <span>Hvad er inkluderet i rengøringen efter tømning?</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-[#3A302B]">
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Aftørring af alle døre, karme, fodpaneler og stikkontakter</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Grundig rengøring af køkken, skabe (ind- og udvendigt) samt hårde hvidevarer</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Afkalkning og grundig sanitet i badeværelse og toilet</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Komplet støvsugning og gulvvask i samtlige rum</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Miljøvenlige rengøringsmidler som skåner materialerne</span>
              </div>
              <div className="flex items-start gap-2.5 bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF]">
                <i className="fa-solid fa-check text-[#887055] mt-1 shrink-0" aria-hidden="true"></i>
                <span>Fotodokumentation og overdragelsesgaranti</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* PRICING INFO SECTION */}
      <section id="priser" className="py-14 lg:py-20 bg-[#F4EFE6] border-y border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFFDF9] rounded-3xl p-8 sm:p-12 border border-[#EADFCF] shadow-sm max-w-4xl mx-auto text-center space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-[#EADFCF] text-[#6B5640] flex items-center justify-center text-2xl mx-auto">
              <i className="fa-solid fa-tag" aria-hidden="true"></i>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#887055]">
                Priser & Tilbud
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-heading text-[#2C2523]">
                Priser for tømning af dødsbo & rengøring
              </h2>
            </div>

            <p className="text-sm sm:text-base text-[#5C524C] leading-relaxed max-w-2xl mx-auto">
              Prislisten for tømning af dødsbo og efterfølgende rengøring opdateres her på siden snarest. Da opgavernes omfang varierer betydeligt afhængigt af boligens størrelse, mængden af indbo og rengøringsbehovet, udarbejder vi altid et uforpligtende tilbud tilpasset den konkrete opgave.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 text-xs sm:text-sm text-[#2C2523] max-w-2xl mx-auto text-left">
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-clipboard-check text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Gratis og uforpligtende besigtigelse</span>
              </div>
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-file-contract text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Fast, gennemskuelig aftale uden overraskelser</span>
              </div>
              <div className="bg-[#FAF7F2] p-4 rounded-xl border border-[#EADFCF] flex items-center gap-3">
                <i className="fa-solid fa-clock text-[#887055] text-lg shrink-0" aria-hidden="true"></i>
                <span className="font-medium">Hurtig udrykning og fleksibel tidsplan</span>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <a
                href="#bestil-form"
                className="bg-[#3A302B] hover:bg-[#231C19] text-[#FFFDF9] px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2 shadow-xs"
              >
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
                <span>Indhent tilbud eller bestil her</span>
              </a>
              <a
                href={`tel:${BUSINESS_INFO.phone}`}
                className="bg-[#EADFCF] hover:bg-[#D8C7B0] text-[#2C2523] px-6 py-3 rounded-xl font-semibold text-sm transition-colors border border-[#D8C7B0] flex items-center gap-2"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                <span>Ring 40 94 75 40</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS & ENVIRONMENT */}
      <section className="py-12 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFFDF9] p-6 sm:p-8 rounded-2xl border border-[#EADFCF] space-y-4">
            <div className="flex items-center gap-2.5 text-[#2C2523] font-bold font-heading">
              <i className="fa-solid fa-leaf text-[#887055]" aria-hidden="true"></i>
              <span>Miljøvenlige rengøringsprodukter</span>
            </div>
            <div className="text-sm text-[#4A403A] space-y-2">
              <p>{PRODUCTS_AND_EQUIPMENT.intro}</p>
              <p className="text-xs sm:text-sm text-[#5C524C]">{PRODUCTS_AND_EQUIPMENT.subIntro}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ORDER / INQUIRY FORM */}
      <section id="bestil-form" className="py-16 lg:py-24 bg-[#FAF7F2] border-t border-[#EADFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GeneralContactForm defaultService="Tømning af dødsbo, derefter rengøring" />
        </div>
      </section>
    </>
  );
}
